/**
 * Theme Territoriale - Auth Register Module
 * File: auth-register.js
 * 
 * Description: Modulo JS per la gestione della registrazione e login, con focus su:
 * - Feedback live forza password
 * - Spinner di caricamento sui form di registrazione/login
 * - Gestione del "tasto indietro" del browser per evitare problemi con la cache (bfcache)
 *
 * NOTA: Questo modulo è incluso in scripts.js e viene inizializzato solo se rileva la presenza dei form di autenticazione/registrazione, per ottimizzare le performance.
 * Se in futuro si aggiungono nuovi form, assicurarsi che abbiano gli ID corretti o aggiornare il selettore.
 *
 * @since 2.0.0
 */


/**
 * Inizializza la validazione e il feedback live per la registrazione.
 * 
 * @returns {void}
 */
export const initAuthRegister = () => {

    // Cerchiamo uno qualsiasi dei form di registrazione o login
    const authForms = document.querySelectorAll(
        '#cci-auth-form-register, ' +
        '#cci-register-cf-verify-form-step-1, ' +
        '#cci-register-cf-verify-form-step-2, ' +
        '#cci-auth-form-login'
    );

    if (authForms.length === 0) return;

    console.log('👍 Auth & Register → inizializzazione in corso...');

    // 1. Gestione Loading Spinner al submit
    authForms.forEach(form => {
        form.addEventListener('submit', () => {
            // Se il form NON è valido non attiviamo lo spinner
            if (form.checkValidity && !form.checkValidity()) {
                return;
            }

            const submitBtn = form.querySelector('button[type="submit"]');
            if (submitBtn) {
                const spinner = submitBtn.querySelector('.spinner-border');
                if (spinner) {
                    spinner.classList.remove('d-none');
                }
                // Per feedback visuale immediato
                submitBtn.classList.add('disabled');

                // Breve timeout
                setTimeout(() => {
                    submitBtn.disabled = true;
                }, 50);
            }
        });
    });

    // 2. Feedback Forza Password (solo se siamo in uno step con password)
    const passInput = document.getElementById('user_pass');
    const strengthBar = document.getElementById('password-strength-bar');
    const strengthText = document.getElementById('password-strength-text');

    if (passInput && strengthBar && strengthText) {
        passInput.addEventListener('input', () => {
            const val = passInput.value;
            const result = checkPasswordStrength(val);

            strengthBar.style.width = result.percent + '%';
            strengthBar.style.backgroundColor = result.color;
            strengthText.innerText = val ? result.message : '';
            strengthText.style.color = result.color;
        });
    }

    /**
     * Gestione "Tasto Indietro" del browser.
     * Forza il ricaricamento della pagina se caricata dalla cache (bfcache).
     * 
     * Questo è importante per evitare che, dopo la registrazione o il login, l'utente torni indietro e veda una versione "vecchia" del form, con dati non aggiornati o messaggi di errore.
     * 
     * Nota: Questo evento viene attivato solo quando la pagina viene caricata dalla cache del browser (bfcache), non su un normale refresh o primo caricamento.
     * 
     * Per testare:
     * 1. Compila e invia il form di registrazione/login.
     * 2. Dopo la risposta del server, clicca sul tasto indietro del browser.
     * 3. La pagina dovrebbe ricaricarsi automaticamente, mostrando il form vuoto o aggiornato, invece di una versione cache con dati vecchi.
     * 
     * ⚠️ Attenzione: Questo comportamento dipende dal browser e dalle sue politiche di caching. La maggior parte dei browser moderni supporta questo evento, ma è sempre bene testare su più browser.
     * 
     * Ulteriori risorse:
     * - https://web.dev/bfcache/#pageshow-event
     * - https://developer.mozilla.org/en-US/docs/Web/API/Window/pageshow_event
     * 
     * In futuro, se si riscontrano problemi con questo approccio, si potrebbe considerare l'uso di un polling o di un evento custom per verificare la presenza di dati obsoleti e forzare il refresh.
     * 
     */
    window.addEventListener('pageshow', (event) => {
        if (event.persisted) {
            window.location.reload();
        }
    });
};


/**
 * Calcola la forza della password in base alla policy:
 * 12+ char, Upper, Number, Special.
 * 
 * Restituisce un oggetto con:
 * - percent: per la barra di forza (25% per ogni criterio)
 * - color: colore da usare (rosso, giallo, azzurro, verde)
 * - message: testo da mostrare all'utente, con indicazione di eventuali criteri mancanti
 * 
 * Esempio di utilizzo:
 * const result = checkPasswordStrength('P@ssw0rd123');
 * console.log(result.percent); // 100
 * console.log(result.color);   // '#198754'
 * console.log(result.message); // 'Ottima! Password robusta.'
 * 
 * 
 * @return {Object} { percent: number, color: string, message: string }
 */
function checkPasswordStrength(password) {
    let score = 0;

    if (!password) return { percent: 0, color: '#eee', message: '' };

    // Lunghezza minima 12
    if (password.length >= 12) score++;

    // Maiuscole
    if (/[A-Z]/.test(password)) score++;

    // Numeri
    if (/[0-9]/.test(password)) score++;

    // Simboli
    if (/[^A-Za-z0-9]/.test(password)) score++;

    // Mappatura risultati
    let missing = [];
    if (password.length < 12) missing.push("12+ caratteri");
    if (!/[A-Z]/.test(password)) missing.push("una maiuscola");
    if (!/[0-9]/.test(password)) missing.push("un numero");
    if (!/[^A-Za-z0-9]/.test(password)) missing.push("un simbolo");

    const missingText = missing.length > 0 ? ` (manca: ${missing.join(', ')})` : '';

    switch (score) {
        case 0:
        case 1:
            return { percent: 25, color: '#dc3545', message: 'Molto debole' + missingText };
        case 2:
            return { percent: 50, color: '#ffc107', message: 'Debole' + missingText };
        case 3:
            return { percent: 75, color: '#0dcaf0', message: 'Buona' + missingText };
        case 4:
            return { percent: 100, color: '#198754', message: 'Ottima! Password robusta.' };
        default:
            return { percent: 0, color: '#eee', message: '' };
    }
}
